import AirObstacle from '../actors/AirObstacle.js'
import GroundObstacle from '../actors/GroundObstacle.js'
import Cloud from '../actors/Cloud.js'
import Jib from '../actors/Jib.js'
import sprites from '../sprites.js'
import {
	getImageData,
} from '../utils.js'
import GameRunner from './GameRunner.js'
import "nette.ajax.js";

export default class JibGame extends GameRunner {
	constructor() {
		super()

		this.canvasWrapper = document.getElementById('canvas-wrapper')
		this.width = window.innerWidth
		this.height = window.innerWidth
		this.id = parseInt(this.canvasWrapper.hasAttribute('data-id') ? this.canvasWrapper.dataset.id : 0);
		this.canvas = this.createCanvas(this.width, this.height);
		this.canvasCtx = this.canvas.getContext('2d')
		this.spriteImage = null
		this.spriteImageData = null
		this.obstacles = window.app.obstacles || {ground: [], air: []}

		/*
		 * units
		 * fpa: frames per action
		 * ppf: pixels per frame
		 * px: pixels
		 */
		this.defaultSettings = {
			bgSpeed: 8, // ppf
			airObstacleSpeed: 9, // ppf
			airObstacleWingsRate: 8, // fpa
			cloudSpeed: 4, // ppf
			jibGravity: 1, // ppf
			jibGroundOffset: 4, // px
			jibLegsRate: 3, // fpa
			jibLift: 15, // ppf
			scoreIncreaseRate: 3, // fpa
		}

		this.state = {
			settings: {...this.defaultSettings},
			airObstacles: [],
			groundObstacles: [],
			clouds: [],
			jibs: [],
			gameOver: false,
			backgroundX: 0,
			backgroundY: 0,
			groundX: 0,
			groundY: 0,
			isRunning: false,
			level: 0,
			score: {
				blinkFrames: 0,
				blinks: 0,
				isBlinking: false,
				value: 0,
			},
		}

		this.onGameFrame = null;
		this.onGameEnd = null;
	}

	// ref for canvas pixel density:
	// https://developer.mozilla.org/en-US/docs/Web/API/Window/devicePixelRatio#correcting_resolution_in_a_%3Ccanvas%3E
	createCanvas(width, height) {
		const canvas = document.createElement('canvas')
		const ctx = canvas.getContext('2d')
		const scale = window.devicePixelRatio

		this.width = width
		this.height = height
		canvas.style.width = width + 'px'
		canvas.style.height = height + 'px'
		canvas.width = Math.floor(width * scale)
		canvas.height = Math.floor(height * scale)
		ctx.scale(scale, scale)

		this.canvasWrapper.appendChild(canvas);
		return canvas
	}

	preload() {
		const spriteImage = document.getElementById('canvas-sprite')
		this.spriteImage = spriteImage
		this.spriteImageData = getImageData(spriteImage)
		this.createJib(this.id)

		this.state.groundY = this.height - sprites.ground.h / 2
	}

	createJib(id) {
		const {settings} = this.state
		const jib = new Jib(this.spriteImageData)

		jib.legsRate = settings.jibLegsRate
		jib.lift = settings.jibLift
		jib.gravity = settings.jibGravity
		jib.x = 25
		jib.baseY = this.height - settings.jibGroundOffset
		jib.invincible = this.debug

		this.state.jibs[id] = jib
	}

	onFrame() {
		const {state} = this

		this.drawBackground()
		this.drawGround()
		// this.drawClouds()
		this.drawJib()
		this.drawScore()

		if (state.isRunning) {
			this.drawGroundObstacles()
			this.drawAirObstacles()

			if (state.jibs[this.id].hits([state.groundObstacles[0], state.airObstacles[0]])) {
				this.kill()
			}

			if (state.gameOver) {
				this.endGame()
			} else {
				this.updateScore()
			}
		}

		if (typeof this.onGameFrame === 'function') {
			this.onGameFrame();
		}
	}

	kill() {
		const {state} = this
		const jib = state.jibs[this.id]

		jib.kill();
		state.gameOver = true
	}

	onInput(type) {
		const {state} = this

		switch (type) {
			case 'j': {
				state.jibs[this.id].jump()
				break
			}

			case 'd': {
				state.jibs[this.id].duck(true)
				break
			}

			case 's': {
				state.jibs[this.id].duck(false)
				break
			}
		}
	}

	resetGame() {
		const {state} = this

		Object.assign(state, {
			settings: {...this.defaultSettings},
			airObstacles: [],
			groundObstacle: [],
			gameOver: false,
			isRunning: true,
			level: 0,
			score: {
				blinkFrames: 0,
				blinks: 0,
				isBlinking: false,
				value: 0,
			},
		})

		this.start()
	}

	endGame() {
		this.state.isRunning = false
		this.drawScore()
		this.stop()

		if (typeof this.onGameEnd === 'function') {
			this.onGameEnd(this);
		}
	}

	increaseDifficulty() {
		const {airObstacles, groundObstacles, clouds, jibs, settings} = this.state
		const {bgSpeed, groundObstacleSpawnRate, jibLegsRate} = settings
		const {level} = this.state

		if (level > 4 && level < 8) {
			settings.bgSpeed++
			settings.airObstacleSpeed = settings.bgSpeed * 0.8
		} else if (level > 7) {
			settings.bgSpeed = Math.ceil(bgSpeed * 1.1)
			settings.airObstacleSpeed = settings.bgSpeed * 0.9
			settings.groundObstacleSpawnRate = Math.floor(groundObstacleSpawnRate * 0.98)

			if (level > 7 && level % 2 === 0 && jibLegsRate > 3) {
				settings.jibLegsRate--
			}
		}

		for (const airObstacle of airObstacles) {
			airObstacle.speed = settings.airObstacleSpeed
		}

		for (const groundObstacle of groundObstacles) {
			groundObstacle.speed = settings.bgSpeed
		}

		for (const cloud of clouds) {
			cloud.speed = settings.bgSpeed
		}

		jibs[this.id].legsRate = settings.jibLegsRate
	}

	updateScore() {
		const {state} = this

		if (this.frameCount % state.settings.scoreIncreaseRate === 0) {
			const oldLevel = state.level

			state.score.value++
			state.level = Math.floor(state.score.value / 100)

			if (state.level !== oldLevel) {
				this.increaseDifficulty()
			}
		}
	}

	drawBackground() {
		this.canvasCtx.fillRect(0, 0, this.width, this.height);

		const {state} = this
		const {bgSpeed} = state.settings
		const groundImgWidth = sprites.background.w

		this.paintBackground('background', state.backgroundX, 0)
		state.backgroundX -= (bgSpeed - 6)

		// append second image until first is fully translated
		if (state.backgroundX <= -groundImgWidth + this.width) {
			this.paintBackground('background', state.backgroundX + groundImgWidth, 0)

			if (state.backgroundX <= -groundImgWidth) {
				state.backgroundX = -bgSpeed
			}
		}
	}

	drawGround() {
		const {state} = this
		const {bgSpeed} = state.settings
		const groundImgWidth = sprites.ground.w / 2

		this.paintSprite('ground', state.groundX, state.groundY)
		state.groundX -= bgSpeed

		// append second image until first is fully translated
		if (state.groundX <= -groundImgWidth + this.width) {
			this.paintSprite('ground', state.groundX + groundImgWidth, state.groundY)

			if (state.groundX <= -groundImgWidth) {
				state.groundX = -bgSpeed
			}
		}
	}

	// drawClouds() {
	// 	const {clouds, settings} = this.state
	//
	// 	this.progressInstances(clouds)
	// 	if (this.frameCount % settings.cloudSpawnRate === 0) {
	// 		const newCloud = new Cloud()
	// 		newCloud.speed = settings.bgSpeed
	// 		newCloud.x = this.width
	// 		newCloud.y = randInteger(20, 80)
	// 		clouds.push(newCloud)
	// 	}
	// 	this.paintInstances(clouds)
	// }

	drawJib() {
		const game = this;
		const {state} = this;
		const {bgSpeed} = state.settings
		const jib = state.jibs[this.id];
		jib.nextFrame()
		game.paintSprite(jib.sprite, jib.x, jib.y)

		if (jib.dead) {
			jib.x -= bgSpeed
		}
	}

	drawGroundObstacles() {
		const {state} = this
		const {groundObstacles, settings} = state

		this.progressInstances(groundObstacles)

		if (this.obstacles.hasOwnProperty('ground') && this.obstacles.ground.hasOwnProperty(this.frameCount)) {
			const newGroundObstacle = new GroundObstacle(this.spriteImageData, this.obstacles.ground[this.frameCount])
			newGroundObstacle.speed = settings.bgSpeed
			newGroundObstacle.x = 800
			newGroundObstacle.y = this.height - newGroundObstacle.height - 5
			groundObstacles.push(newGroundObstacle)
		}

		this.paintInstances(groundObstacles)
	}

	drawAirObstacles() {
		const {airObstacles, settings} = this.state

		this.progressInstances(airObstacles)

		if (this.obstacles.hasOwnProperty('air') && this.obstacles.air.hasOwnProperty(this.frameCount)) {
			const newAirObstacle = new AirObstacle(this.spriteImageData, this.obstacles.air[this.frameCount])
			newAirObstacle.speed = settings.airObstacleSpeed
			newAirObstacle.wingsRate = settings.airObstacleWingsRate
			newAirObstacle.x = 800
			newAirObstacle.y =
				this.height -
				newAirObstacle.maxAirObstacleHeight -
				5 -
				sprites.jibDuckLeftLeg.h / 2 -
				settings.jibGroundOffset + 10
			airObstacles.push(newAirObstacle)
		}

		this.paintInstances(airObstacles)
	}

	drawScore() {
		const {state} = this
		const {isRunning, score} = state
		let shouldDraw = true
		let drawValue = score.value

		if (isRunning && shouldDraw) {
			document.getElementById('score').innerText = 'Score: ' + drawValue;
		}
	}

	/**
	 * For each instance in the provided array, calculate the next
	 * frame and remove any that are no longer visible
	 * @param {Actor[]} instances
	 */
	progressInstances(instances) {
		for (let i = instances.length - 1; i >= 0; i--) {
			if (!instances.hasOwnProperty(i)) {
				continue;
			}

			const instance = instances[i]
			instance.nextFrame()
			if (instance.rightX <= 0) {
				instances.splice(i, 1)
			}
		}
	}

	/**
	 * @param {Actor[]} instances
	 */
	paintInstances(instances) {
		for (const instance of instances) {
			this.paintSprite(instance.sprite, instance.x, instance.y)
		}
	}

	paintBackground(spriteName, dx, dy) {
		const {h, w, x, y} = sprites[spriteName]
		this.canvasCtx.drawImage(this.spriteImage, x, y, w, h, dx, dy, w, h)
	}

	paintSprite(spriteName, dx, dy) {
		const {h, w, x, y} = sprites[spriteName]
		this.canvasCtx.drawImage(this.spriteImage, x, y, w, h, dx, dy, w / 2, h / 2)
	}

}
